<template>
  <b-modal
    :active="Boolean(prompt)"
    :width="760"
    :can-cancel="['escape', 'outside']"
    custom-class="track-prompt-modal"
    @close="close"
  >
    <div
      v-if="prompt"
      class="track-prompt"
      :class="{ 'has-cover': Boolean(cover) }"
    >
      <button
        type="button"
        class="track-prompt-close"
        aria-label="Close"
        @click="close"
      />
      <div
        v-if="cover"
        class="track-prompt-cover"
      >
        <img
          :src="cover"
          alt=""
        >
      </div>
      <div class="track-prompt-body">
        <div class="track-prompt-heading">
          <h2>{{ copy.heading }}</h2>
        </div>
        <div class="track-prompt-content">
          <p>{{ copy.text }}</p>
          <button
            type="button"
            class="track-prompt-button"
            @click="confirm"
          >
            {{ copy.button }}
          </button>
          <p
            v-if="isSubscribe"
            class="track-prompt-small"
          >
            Just need this one?
            <a
              href="#"
              @click.prevent="licenseTrack"
            >License this track</a>
          </p>
          <p
            v-else
            class="track-prompt-small"
          >
            Already have an account?
            <a
              href="#"
              @click.prevent="logIn"
            >Log in</a>
          </p>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex'
import { TRIAL_DAYS, goToSubscribe } from '@/utils/subscribeFlow.js'
import { addTrackToCart } from '@/utils/trackLicensing.js'

// What each track action needs an account for. Shown before Create account
// when someone logged out tries one, alongside the track's cover.
const SIGNUP_COPY = {
  download: {
    heading: 'Found the one?',
    text: 'Create a free account to license this track for your next video.'
  },
  preview: {
    heading: 'Try it in your edit',
    text: 'Create a free account to download a watermarked preview and hear how this track sits with your footage.'
  },
  favourite: {
    heading: 'Save it for later',
    text: 'Create a free account to favourite tracks and find them again whenever you need them.'
  },
  collection: {
    heading: 'Start a collection',
    text: 'Create a free account to gather tracks into collections for each of your projects.'
  },
  play: {
    heading: 'Keep listening',
    text: 'Create a free account to keep listening. It’s free to browse and preview the whole catalogue.'
  }
}

// Asks for an account (logged out) or a plan (logged in without one) when someone
// tries a track action that needs it, showing the track they picked. Opened with
// common/showTrackPrompt, via user/showSignupModal for the account case.
export default {
  name: 'TheTrackPromptModal',

  computed: {
    ...mapGetters({
      prompt: 'common/trackPrompt',
      trialMode: 'subscriptions/createSubscriptionWithTrial',
      currentTeam: 'user/currentTeam'
    }),

    isSubscribe: function () {
      return this.prompt.reason === 'subscribe'
    },

    cover: function () {
      const track = this.prompt && this.prompt.track
      return (track && (track.cover_medium || track.cover_thumbnail)) || null
    },

    copy: function () {
      const plans = 'Every plan includes unlimited downloads and a licence that covers where your videos go.'
      if (this.isSubscribe && this.trialMode) {
        return {
          heading: 'Download now with a free trial',
          text: `${plans} Free for ${TRIAL_DAYS} days, cancel anytime.`,
          button: `Get ${TRIAL_DAYS} days free`
        }
      }
      if (this.isSubscribe) {
        return {
          heading: 'Download now with a subscription',
          text: `${plans} Switch plans or cancel at any time.`,
          button: 'See plans'
        }
      }
      const copy = SIGNUP_COPY[this.prompt.reason] || SIGNUP_COPY.download
      const trial = this.trialMode && this.prompt.reason === 'download'
        ? ` Every plan starts with a ${TRIAL_DAYS}-day free trial.`
        : ''
      return { ...copy, text: copy.text + trial, button: 'Create free account' }
    }
  },

  watch: {
    $route () {
      this.close()
    }
  },

  methods: {
    close () {
      this.$store.dispatch('common/hideTrackPrompt')
    },

    confirm () {
      const subscribe = this.isSubscribe
      this.close()
      if (subscribe) {
        goToSubscribe(this.$router, this.currentTeam)
      } else {
        this.$store.dispatch('user/showSignupModal')
      }
    },

    logIn () {
      this.close()
      this.$store.dispatch('auth/showLoginModal')
    },

    licenseTrack () {
      const track = this.prompt.track
      this.close()
      addTrackToCart(this, track)
    }
  }
}
</script>

<style lang="scss">
@import "../assets/scss/_variables.scss";

$close-icon: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M3.5 3.5l9 9M12.5 3.5l-9 9' fill='none' stroke='%23142136' stroke-width='1.6' stroke-linecap='round'/%3E%3C/svg%3E";

// .modal as well, to beat Bulma's mobile margins, which load after this.
.modal.track-prompt-modal .modal-content {
  overflow: visible;
  @media (max-width: 640px) {
    width: calc(100% - 32px);
    margin: 0 auto;
  }
}

// Three tones: the packshot on light grey, the heading on a muted coral blush,
// and the rest on white.
.track-prompt {
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  overflow: hidden;
  border-radius: 16px;
  background: $white;
  color: $bluegrey1;

  &.has-cover {
    grid-template-columns: 300px 1fr;
  }

  .track-prompt-cover {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
    background: $bluegrey6;
    img {
      display: block;
      width: 100%;
      aspect-ratio: 1;
      object-fit: cover;
      border-radius: 6px;
      box-shadow: 0 10px 30px rgba($bluegrey1, 0.18);
    }
  }

  .track-prompt-body {
    display: flex;
    flex-direction: column;
  }

  .track-prompt-heading {
    padding: 44px 80px 24px 48px;
    background: hsl(11, 55%, 95%);

    h2 {
      margin: 0;
      color: $bluegrey1;
      font-weight: 500;
      font-size: 32px;
      line-height: 1.12;
      letter-spacing: -0.01em;
    }
  }

  .track-prompt-content {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: 24px 48px 36px;

    > p {
      margin: 0 0 24px;
      color: rgb(84, 95, 113);
      font-size: 16px;
      line-height: 1.5;
    }

    > p.track-prompt-small {
      margin: 16px 0 0;
      font-size: 13px;
      a {
        color: $bluegrey1;
        text-decoration: underline;
        text-underline-offset: 2px;
      }
    }
  }

  .track-prompt-button {
    height: 48px;
    padding: 0 28px;
    border: 0;
    border-radius: 999px;
    background: $accent;
    color: $white;
    font: 500 15px $family-sans-serif;
    cursor: pointer;
    transition: background 0.12s ease-out;
    &:hover {
      background: darken($accent, 6%);
    }
  }

  .track-prompt-close {
    position: absolute;
    top: 18px;
    right: 18px;
    z-index: 1;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent url($close-icon) center / 16px no-repeat;
    cursor: pointer;
    &:hover {
      background-color: rgba($bluegrey1, 0.08);
    }
  }

  button:focus-visible,
  a:focus-visible {
    outline: 2px solid $bluegrey1;
    outline-offset: 2px;
  }

  @media (max-width: 640px) {
    &.has-cover {
      grid-template-columns: 1fr;
    }

    .track-prompt-cover {
      padding: 32px 32px 28px;
      img {
        width: 160px;
      }
    }

    .track-prompt-heading {
      padding: 22px 24px 18px;
      text-align: center;
      h2 {
        font-size: 26px;
      }
    }

    .track-prompt-content {
      align-items: stretch;
      padding: 20px 24px 28px;
      text-align: center;
    }
  }

  // Without a cover the heading band is at the top, under the close button.
  &:not(.has-cover) .track-prompt-heading {
    padding-top: 56px;
  }
}
</style>
